import {
	RemoveShoppingCartOutlined
} from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Container,
	Divider,
	Link,
	Skeleton,
	Typography
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";

import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import CartTable from "../../components/CartTable/CartTable";
import CartUpload from "../../components/CartUpload/CartUpload";
import PageTitle from "../../components/PageTitle/PageTitle";
import { ASSETS_BASE_URL, DEFAULT_LAYOUT } from "../../config";
import {
	ROUTES
} from "../../constants";
import { useAuth } from "../../context/authContext";
import { toast } from "../../context/ToastContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { getCartCTP, getCartRecommendations, getOptions, getProductsById } from "../../services/cartService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import "../Cart/Cart.css";

// ─── FIX 1: Module-level image cache ────────────────────────────────────────
// Persists across re-renders and page navigations within the session.
// Eliminates redundant HEAD requests for product images already checked.
const imageCache = new Map();

// Helper function to get product ID, handling both 'ProductID' and 'product_id'
const getNormalizedProductId = (item) => item?.ProductID || item?.product_id;

const getBomNodeLevel = (node = {}) => {
	const rawLevel = node?.bomLevel ?? node?.bom_level ?? node?.level;
	const parsedLevel = Number(rawLevel);
	return Number.isFinite(parsedLevel) && parsedLevel >= 0 ? parsedLevel : null;
};

const getBomNodeQuantity = (node = {}) => {
	const rawQty = node?.bomQuantity ?? node?.quantity ?? node?.qty ?? node?.bom_quantity ?? node?.bom_qty;
	const parsedQty = Number(rawQty);
	return Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
};

const buildBomNodeDetailsMap = (bomTree = [], machineId) => {
	const details = new Map();
	const traverse = (nodes = [], parentNode = null, ancestorPath = []) => {
		nodes.forEach((node) => {
			const productId = Number(node?.productId);
			const currentNodeName = node?.productName || node?.product_name || "";
			const nextAncestorPath = currentNodeName ? [...ancestorPath, currentNodeName] : ancestorPath;
			if (Number.isFinite(productId) && productId > 0) {
				const key = `${machineId}:${productId}`;
				const bomLevel = getBomNodeLevel(node);
				const isTopLevelPart = Number(bomLevel) === 1;
				const parentName = isTopLevelPart ? "" : (parentNode?.productName || parentNode?.product_name || "");
				const parentPath = isTopLevelPart ? "" : ancestorPath.join(" > ");
				const bomRef = {
					bom_level: bomLevel,
					bom_qty: getBomNodeQuantity(node),
					assembly_name: parentName,
					bom_path: parentPath,
				};
				const existing = details.get(key);
				if (!existing) details.set(key, { bom_level: bomLevel, bom_refs: [bomRef] });
				else details.set(key, { ...existing, bom_refs: [...(existing.bom_refs || []), bomRef] });
			}
			if (Array.isArray(node?.children) && node.children.length > 0) {
				traverse(node.children, node, nextAncestorPath);
			}
		});
	};
	traverse(bomTree, null, []);
	return details;
};

const Cart = () => {
	const { cartItems, removeFromCart, clearCart, updateCart, isInitialized, refreshCart } = useCart();
	const { selectedSite, selectedSiteCurrency, currencySymbol, selectedSiteData } = useSite();
	const { user, hasPermission } = useAuth();
	const { stockImageUrl } = useBrandingAssets();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [cartProducts, setCartProducts] = useState([]);
	const [groupedCartProducts, setGroupedCartProducts] = useState([]);
	const [isLocalInitialized, setIsLocalInitialized] = useState(false);
	const [recommendedProducts, setRecommendedProducts] = useState([]);
	const [showDisclaimerCheckout, setShowDisclaimerCheckout] = useState(false);
	const [quantityInputs, setQuantityInputs] = useState({});
	const [modelViewerSummary, setModelViewerSummary] = useState(null);
	const [pendingMixedSummary, setPendingMixedSummary] = useState(null);
	const [appOptions, setAppOptions] = useState({
		currency: "USD",
		minOrderAmount: 0,
		maxOrderQty: 999,
		creditCardFee: 2.5,
		taxRate: 5,
		shippingCost: 10,
		leadTime: 3,
	});

	const prodsRef = useRef(null);
	const ctpDebounceRef = useRef(null);
	const ctpReqSeq = useRef(0);
	const recDebounceRef = useRef(null);
	const recReqSeq = useRef(0);

	const { t } = useTranslation();
	const maxQtyAllowed = 999;
	const canPlaceOrder = hasPermission("order_management.create_order");
	const canCreateOrderSchedule = hasPermission("order_management.create_order_schedule");
	const navigate = useNavigate();
	const location = useLocation();

	const triggerToast = (message, severity = "info") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};

	const handleNavigateToProduct = (url) => {
		navigate(url);
	};

	// ─── FIX 2: Cached image checker ──────────────────────────────────────────
	// Uses the module-level imageCache Map. On first request for a productNumber,
	// fires the HEAD request and stores the result. All subsequent calls for the
	// same productNumber resolve instantly from cache without any network request.
	const joinUrl = (base, path) => `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(/^\/+/, "")}`;

	const buildProductImageCandidates = (productNumber) => {
		const base = String(ASSETS_BASE_URL || "").replace(/\/+$/, "");
		const bases = base.endsWith("/assets") ? [base, base.replace(/\/assets$/i, "")] : [base, `${base}/assets`];
		const extensions = ["JPG", "jpg", "png", "jpeg", "JPEG"];
		const candidates = [];

		for (const b of bases) {
			for (const ext of extensions) {
				candidates.push(joinUrl(b, `ProductImages/${productNumber}.${ext}`));
			}
		}
		return Array.from(new Set(candidates));
	};

	const resolveStockFallback = () => {
		if (stockImageUrl) return stockImageUrl;
		const base = String(ASSETS_BASE_URL || "").replace(/\/+$/, "");
		return base.endsWith("/assets") ? joinUrl(base, "stock.png") : joinUrl(base, "assets/stock.png");
	};

	useEffect(() => {
		imageCache.clear();
	}, [stockImageUrl]);

	const checkImageExists = async (productNumber) => {
		if (imageCache.has(productNumber)) return imageCache.get(productNumber);

		try {
			const candidates = buildProductImageCandidates(productNumber);
			for (const url of candidates) {
				const response = await fetch(url, { method: "HEAD" });
				if (response.ok) {
					imageCache.set(productNumber, url);
					return url;
				}
			}
		} catch (error) {
			console.warn("Error while resolving product image path, using default image.");
		}

		const fallback = resolveStockFallback();
		imageCache.set(productNumber, fallback);
		return fallback;
	};

	const productsInCart = useMemo(() => {
		if (selectedSite) {
			return cartItems?.filter(
				(item) => item !== null && item !== undefined && item.Site === selectedSite
			) || [];
		}
		return [];
	}, [cartItems, selectedSite]);

	const uniqueProductIds = useMemo(
		() => [...new Set(productsInCart.map((p) => getNormalizedProductId(p)).filter(Boolean))],
		[productsInCart]
	);

	const cartProductIds = useMemo(
		() => uniqueProductIds.slice().sort().join(","),
		[uniqueProductIds]
	);

	const fetchCTPForItems = async (cartItems) => {
		try {
			const req = {
				items: cartItems.map((cp) => ({
					product_id: getNormalizedProductId(cp),
					quantity: cp?.Quantity || 1,
				})),
				site_number: selectedSite,
			};
			const resp = await getCartCTP(req);
			const data = (resp?.data || []).filter((d) => d !== null && d !== undefined);
			const byId = new Map(data.map((d) => [String(d.product_id), d.ctp]));
			return cartItems
				.map((cp) => {
					if (!cp) return null;
					const pid = String(getNormalizedProductId(cp));
					const ctp = byId.get(pid);
					return ctp ? { ...cp, ctp } : cp;
				})
				.filter((cp) => cp !== null);
		} catch (e) {
			console.error("Failed to fetch CTP:", e);
			return cartItems;
		}
	};

	// Debounced CTP updater: runs in background after cart is already visible
	const scheduleCTPRefresh = (items) => {
		if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
		const seq = ++ctpReqSeq.current;
		ctpDebounceRef.current = setTimeout(async () => {
			try {
				const withCTP = await fetchCTPForItems(items);
				if (seq === ctpReqSeq.current) {
					setCartProducts(withCTP);
				}
			} catch (e) {
				// ignore stale responses
			}
		}, 400);
	};

	// ─── FIX 3: fetchOptions extracted and deduplicated ────────────────────────
	// Previously called in two places (standalone useEffect + inside fetchProducts).
	// Now it lives here as a standalone async function called ONCE inside
	// fetchProducts. The redundant standalone useEffect has been removed entirely.
	const fetchOptions = async () => {
		try {
			const response = await getOptions();
			const responseData = response.data.options;
			setAppOptions(responseData);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchProducts = async () => {
		setIsLocalInitialized(false);
		setLoading(true);
		setError(null);

		// Single call to fetchOptions here — the standalone useEffect below is removed
		await fetchOptions();

		if (productsInCart.length === 0) {
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true);
			return;
		}

		try {
			const productIds = uniqueProductIds;
			const response = await getProductsById(productIds, selectedSite);
			const productData = (response?.data || []).filter((p) => p !== null && p !== undefined);

			const machineIds = Array.from(
				new Set(
					productsInCart
						.map((item) => {
							const matchedProduct = productData.find(
								(product) =>
									parseInt(product.product_id) === parseInt(getNormalizedProductId(item))
							);
							return matchedProduct?.machine_id || item?.machine_id || item?.machineId || null;
						})
						.map((id) => Number(id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			);

			// BOM fetches are already parallelized via Promise.all — no change needed here
			const bomNodeDetailEntries = await Promise.all(
				machineIds.map(async (machineId) => {
					try {
						const bomResponse = await getProductsByMachineId({
							machine_id: machineId,
							site_number: selectedSite,
						});
						if (!bomResponse?.status) return null;
						const { bomTree } = normalizeMachineProductsResponse(bomResponse);
						return buildBomNodeDetailsMap(bomTree || [], machineId);
					} catch (error) {
						console.warn(`Failed to fetch BOM quantities for machine ${machineId}`, error);
						return null;
					}
				})
			);
			const machineDetailsResponse = machineIds.length
				? await getMachinesByIds({
					machines: machineIds,
					site_number: selectedSite,
					site_id: selectedSiteData?.site_id,
					cus_id: selectedSiteData?.cus_id,
				})
				: null;

			const bomNodeDetailsMap = bomNodeDetailEntries.reduce((acc, entry) => {
				if (!entry) return acc;
				entry.forEach((value, key) => acc.set(key, value));
				return acc;
			}, new Map());
			const machineDetailsMap = new Map(
				(machineDetailsResponse?.data || [])
					.filter((machine) => Number.isFinite(Number(machine?.machine_id)))
					.map((machine) => [Number(machine.machine_id), machine])
			);

			// ─── FIX 4: All per-product work runs concurrently via Promise.all ──────
			// Image checks now benefit from the module-level cache (FIX 2) so
			// subsequent renders skip network entirely. All items are processed in
			// parallel — no serial await chains inside the map.
			let disclaimerFound = false;
			const updatedCartItems = await Promise.all(
				productsInCart.map(async (item) => {
					if (!item) return null;

					const matchedProduct = productData.find(
						(product) =>
							parseInt(product.product_id) === parseInt(getNormalizedProductId(item))
					);

					if (matchedProduct) {
						// checkImageExists is now cached — no duplicate HEAD requests
							const imageSrc = await checkImageExists(matchedProduct.product_number);
						const showDisclaimer = ["30AA", "30BA", "3AAA", "3ABA"].includes(
							matchedProduct.product_class
						);
						const machineId = Number(
							matchedProduct?.machine_id || item?.machine_id || item?.machineId
						);
						const productId = Number(getNormalizedProductId(item));
						const machineData =
							Number.isFinite(machineId) && machineId > 0
								? machineDetailsMap.get(machineId)
								: null;
						const bomNodeDetails =
							Number.isFinite(machineId) && Number.isFinite(productId)
								? bomNodeDetailsMap.get(`${machineId}:${productId}`)
								: null;

						if (showDisclaimer) disclaimerFound = true;

						return {
							...item,
								product_data: matchedProduct,
								machineData: machineData || item?.machineData || null,
							machine_number:
								item?.machine_number ||
								item?.machineNumber ||
								machineData?.machine_number ||
								matchedProduct?.machine_number ||
								"",
							line_name:
								item?.line_name ||
								item?.lineName ||
								machineData?.line_name ||
								matchedProduct?.line_name ||
								"",
								showDisclaimer,
								imageSrc,
								bom_level: bomNodeDetails?.bom_level,
								bom_refs: Array.isArray(item?.bom_refs) && item.bom_refs.length > 0
									? item.bom_refs
									: bomNodeDetails?.bom_refs || [],
							};
					}
					return null;
				})
			);

			// Batch the disclaimer state update — avoid calling setShowDisclaimerCheckout
			// inside the loop (triggers a re-render per item in the old code)
			if (disclaimerFound) setShowDisclaimerCheckout(true);

			const finalCartItems = updatedCartItems.filter((item) => item !== null);

			if (finalCartItems.length > 0) {
				// ─── FIX 5: Render cart immediately, fetch CTP in background ──────────
				// Old code: awaited CTP before setLoading(false) → user saw skeletons
				//           until pricing resolved.
				// New code: setLoading(false) + setIsLocalInitialized(true) fire right
				//           after product data is ready. CTP arrives ~400ms later and
				//           updates grouping quietly without a loading flash.
				setCartProducts(finalCartItems);
				setLoading(false);
				setIsLocalInitialized(true);
				// CTP refresh is fully deferred — cart is already visible at this point
				scheduleCTPRefresh(finalCartItems);
			} else {
				setCartProducts([]);
				setLoading(false);
				setIsLocalInitialized(true);
			}
		} catch (err) {
			setError(t("ERROR_MESSAGES.FAILED_TO_FETCH_DATA", { error: err.message }));
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true);
		}
	};

	// ─── FIX 6: Standalone fetchOptions useEffect removed ─────────────────────
	// The original code had:
	//   useEffect(() => { if (!isInitialized) return; fetchOptions(); }, [isInitialized]);
	// This fired a duplicate options request on every mount because fetchProducts()
	// also called fetchOptions() internally. Removed entirely — fetchProducts handles it.

	useEffect(() => {
		if (!isInitialized) return;

		if (!cartProductIds.length) {
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true);
			return;
		}

		// ─── FIX 7: Normalized ID comparison to prevent spurious re-fetches ──────
		// Old code compared product_data.product_id (numbers) against cartProductIds
		// (strings). If types differed, strict equality always failed → refetch on
		// every render even when nothing changed.
		// New code: both sides are cast to String before comparison.
		const localIds = cartProducts
			.map((p) => String(p.product_data?.product_id))
			.filter(Boolean)
			.sort()
			.join(",");

		if (localIds === cartProductIds) {
			return; // IDs match — cart is already up to date, skip fetch
		}

		fetchProducts();
	}, [cartProductIds, isInitialized]);

	// Recommendations: debounced, runs independently of product fetch
	useEffect(() => {
		if (!isInitialized) return;
		if (!cartProductIds.length || !selectedSite) {
			setRecommendedProducts([]);
			return;
		}

		if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
		const seq = ++recReqSeq.current;
		recDebounceRef.current = setTimeout(async () => {
			try {
				const response = await getCartRecommendations(
					uniqueProductIds,
					selectedSite,
					selectedSiteData?.cus_id,
					selectedSiteData?.site_id
				);
				if (seq !== recReqSeq.current) return;
				const recommendations = Array.isArray(response?.data) ? response.data : [];
				const uniqueRecommendations = [
					...new Map(recommendations.map((item) => [item.pnum, item])).values(),
				];
				setRecommendedProducts(uniqueRecommendations);
			} catch (error) {
				if (seq !== recReqSeq.current) return;
				console.warn("Failed to fetch recommendations:", error);
				setRecommendedProducts([]);
			}
		}, 400);
	}, [cartProductIds, isInitialized, selectedSite, uniqueProductIds]);

	useEffect(() => {
		if (pendingMixedSummary) {
			setModelViewerSummary(pendingMixedSummary);
			setPendingMixedSummary(null);
			if (pendingMixedSummary?.requestCount && !pendingMixedSummary.cartCount) {
				navigate("/requests");
			}
		}
	}, [pendingMixedSummary, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const mvSource = params.get("mvSource");
		if (mvSource) {
			const cartCount = parseInt(params.get("mvCartCount"), 10) || 0;
			const requestCount = parseInt(params.get("mvRequestCount"), 10) || 0;
			setModelViewerSummary({ cartCount, requestCount, source: mvSource });
			setLoading(true);
			setIsLocalInitialized(false);
			refreshCart();
		} else {
			setModelViewerSummary(null);
		}
	}, [location.search, refreshCart]);

	// Cleanup debounce timers on unmount
	useEffect(() => {
		return () => {
			if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
			if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
		};
	}, []);

	// Group cart products by shipping date once CTP data is available
	useEffect(() => {
		if (cartProducts.length > 0) {
			const getgroupedCartProducts = cartProducts.reduce((groups, item) => {
				if (!item || !item.product_data) return groups;
				const { ctp } = item;

				let shippingDate = null;
				if (ctp?.in_stock && ctp.in_stock.qty > 0) {
					shippingDate = ctp.in_stock.shipping_date;
				} else if (ctp?.out_of_stock && ctp?.out_of_stock?.qty > 0) {
					shippingDate = ctp.out_of_stock.shipping_date;
				} else {
					shippingDate = "__loading__";
				}

				if (!shippingDate) shippingDate = "__loading__";
				if (!groups[shippingDate]) groups[shippingDate] = [];
				groups[shippingDate].push(item);

				return groups;
			}, {});

			setGroupedCartProducts(getgroupedCartProducts);
		}
	}, [cartProducts]);

	const parsePrice = (price) => {
		const numPrice = typeof price === "string" ? parseFloat(price) : price;
		return isNaN(numPrice) ? 0 : numPrice;
	};

	const calculateSubtotal = () => {
		return (cartProducts || []).reduce((total, item) => {
			if (!item || !item.product_data) return total;
			const key = `price_${selectedSiteCurrency}`;
			const currencyPrice = item.product_data?.[key];
			const finalPrice = item.product_data?.purchasable ? currencyPrice : 0;
			const price = parsePrice(finalPrice);
			const cartItem = cartItems.find(
				(ci) =>
					getNormalizedProductId(ci) === getNormalizedProductId(item) &&
					ci.Site === selectedSite
			);
			const quantity = cartItem ? cartItem?.Quantity : item?.Quantity;
			return total + price * quantity;
		}, 0);
	};

	const calculateTax = () => calculateSubtotal() * 0;
	const calculateTotal = () => calculateSubtotal() + calculateTax();

	const handleInputChange = (productId, inputValue) => {
		setQuantityInputs((prev) => ({ ...prev, [productId]: inputValue }));
	};

	const handleInputBlur = (productId) => {
		const inputValue = quantityInputs[productId];
		const product = cartProducts.find((p) => getNormalizedProductId(p) === productId);
		if (!product) return;

		const digitsOnly = inputValue ? inputValue.replace(/\D/g, "") : "";

		if (!digitsOnly) {
			setQuantityInputs((prev) => ({ ...prev, [productId]: "1" }));
			updateQuantity(productId, 1, "input");
			triggerToast(t("ALERT_MESSAGES.QUANTITY_CANNOT_BE_EMPTY"), "info");
			return;
		}

		let quantity = parseInt(digitsOnly);
		if (isNaN(quantity)) {
			setQuantityInputs((prev) => ({ ...prev, [productId]: "1" }));
			updateQuantity(productId, 1, "input");
			return;
		}

		let finalQuantity = quantity;
		let showAlert = false;
		let alertMessage = "";

		if (quantity < 1) {
			finalQuantity = 1;
			showAlert = true;
			alertMessage = t("ALERT_MESSAGES.QUANTITY_CANNOT_BE_LESS_THAN_ONE");
		} else if (quantity > maxQtyAllowed) {
			finalQuantity = maxQtyAllowed;
			showAlert = true;
			alertMessage = t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MAX", { maxStock: maxQtyAllowed });
		}

		setQuantityInputs((prev) => ({ ...prev, [productId]: finalQuantity.toString() }));
		updateQuantity(productId, finalQuantity, "input");
		if (showAlert) triggerToast(alertMessage, "warning");
	};

	const handleKeyPress = (productId, event) => {
		if (event.key === "Enter") {
			event.target.blur();
		} else if (event.key === "Escape") {
			const currentItem = cartItems.find(
				(item) => getNormalizedProductId(item) === productId && item.Site === selectedSite
			);
			const currentQuantity = currentItem ? currentItem.Quantity : 1;
			setQuantityInputs((prev) => ({ ...prev, [productId]: currentQuantity.toString() }));
			event.target.blur();
		}
	};

	const handleKeyDown = (productId, event) => {
		const allowedKeys = [
			"Backspace",
			t("UI_TEXT.TOOLTIP_DELETE"),
			"ArrowLeft",
			"ArrowRight",
			"Home",
			"End",
			"Tab",
			"Enter",
			"Escape",
		];
		const isDigit = /^[0-9]$/.test(event.key);
		const isCtrlKey = event.ctrlKey || event.metaKey;
		if (!isDigit && !allowedKeys.includes(event.key) && !isCtrlKey) {
			event.preventDefault();
		}
	};

	const handleRemoveItem = (productId) => {
		removeFromCart(productId);
		setCartProducts((prev) => prev.filter((item) => getNormalizedProductId(item) !== productId));
		setQuantityInputs((prev) => {
			const newState = { ...prev };
			delete newState[productId];
			return newState;
		});
	};

	const handleQuantityChange = (product_id, count) => {
		const currentItem = cartItems.find(
			(item) => getNormalizedProductId(item) === product_id && item.Site === selectedSite
		);
		if (!currentItem) return;
		const newQuantity = parseInt(currentItem.Quantity || 0, 10) + count;
		const source = count > 0 ? "increment" : "decrement";
		updateQuantity(product_id, newQuantity, source);
	};

	const updateQuantity = async (productId, newQuantity, source = "direct") => {
		const cartItem = cartItems.find(
			(item) => getNormalizedProductId(item) === productId && item?.Site === selectedSite
		);
		const product = cartProducts.find((p) => getNormalizedProductId(p) === productId);
		if (!cartItem || !product) return;

		let finalQuantity = newQuantity;
		if (finalQuantity < 1) {
			finalQuantity = 1;
		} else if (finalQuantity > maxQtyAllowed) {
			finalQuantity = maxQtyAllowed;
			if (source === "increment") {
				triggerToast(
					t("ALERT_MESSAGES.MAX_STOCK_LIMIT_REACHED", { maxStock: maxQtyAllowed }),
					"info"
				);
			} else if (source === "input") {
				triggerToast(
					t("ALERT_MESSAGES.MAX_UNITS_ALLOWED_ADJUSTED", { maxStock: maxQtyAllowed }),
					"warning"
				);
			}
		}

		const updatedCartItems = cartItems.map((item) => {
			if (getNormalizedProductId(item) === productId && item?.Site === selectedSite) {
				return { ...item, Quantity: finalQuantity };
			}
			return item;
		});

		const updatedCartProducts = cartProducts.map((item) => {
			if (getNormalizedProductId(item) === productId && item?.Site === selectedSite) {
				return { ...item, Quantity: finalQuantity };
			}
			return item;
		});

		// Optimistic update immediately, CTP recalculates in background
		setCartProducts(updatedCartProducts);
		scheduleCTPRefresh(updatedCartProducts);
		updateCart(updatedCartItems);
		setQuantityInputs((prev) => ({ ...prev, [productId]: finalQuantity.toString() }));
	};

	// ─── Loading / error states ────────────────────────────────────────────────

	const showLoadingState = loading || (productsInCart.length > 0 && cartProducts.length === 0);

	if (showLoadingState) {
		return (
			<Container maxWidth="xl" className="cart-container">
				<PageTitle title="QUOTE" />
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-between",
						gap: "24px",
						mt: 3,
					}}
				>
					<Box sx={{ flex: 1 }}>
						<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: "10px" }}>
							<CardContent>
								<Skeleton variant="text" width={220} height={28} />
								<Divider sx={{ my: 2 }} />
								{[...Array(4)].map((_, idx) => (
									<Box
										key={idx}
										sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}
									>
										<Skeleton
											variant="rectangular"
											width={64}
											height={64}
											sx={{ borderRadius: 1 }}
										/>
										<Box sx={{ flex: 1 }}>
											<Skeleton variant="text" width="60%" height={22} />
											<Skeleton variant="text" width="40%" height={18} />
										</Box>
										<Skeleton
											variant="rectangular"
											width={88}
											height={36}
											sx={{ borderRadius: 1 }}
										/>
										<Skeleton variant="text" width={80} height={22} />
										<Skeleton
											variant="rectangular"
											width={32}
											height={32}
											sx={{ borderRadius: "50%" }}
										/>
									</Box>
								))}
							</CardContent>
						</Card>
					</Box>
					<Box sx={{ flex: 0.4 }}>
						<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: "10px" }}>
							<CardContent>
								<Skeleton variant="text" width={160} height={24} />
								<Divider sx={{ my: 2 }} />
								<Box className="summarySubtotal">
									<Box className="summary-row">
										<Skeleton variant="text" width={120} height={20} />
										<Skeleton variant="text" width={80} height={20} />
									</Box>
								</Box>
								<Box className="summary-row total">
									<Skeleton variant="text" width={100} height={24} />
									<Skeleton variant="text" width={100} height={24} />
								</Box>
								<Box className="summary-row total">
									<Skeleton variant="text" width="100%" height={14} />
								</Box>
								<Box className="summary-actions" sx={{ mt: 2 }}>
									<Skeleton
										variant="rectangular"
										width="100%"
										height={40}
										sx={{ borderRadius: 1 }}
									/>
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Box>
			</Container>
		);
	}

	if (!isLocalInitialized) {
		return (
			<Box className="cart-loader">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	return (
		<>
			<Container
				maxWidth="xl"
				className="cart-container"
				sx={{ pb: 4 }}
				data-tour="cart-part-details"
			>
				<PageTitle title="QUOTE" />
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 1.5, fontStyle: "italic" }}
				>
					* Note: Items in the Quote and its resulting Sales Order might vary. Please refer to the final Sales Order or connect with your sales representative for details.
				</Typography>

				{modelViewerSummary && (
					<Alert severity="info" sx={{ mt: 2 }}>
						<Typography variant="body2">
							{modelViewerSummary.cartCount} part(s) were added to your quote and{" "}
							{modelViewerSummary.requestCount} part(s) were sent to the Request List.
						</Typography>
						<Link
							component={RouterLink}
							to="/requests"
							sx={{ mt: 1, display: "inline-flex", fontWeight: 600 }}
						>
							{t("UI_TEXT.VIEW_REQUEST_LIST")}
						</Link>
					</Alert>
				)}

				{cartProducts.length === 0 && !showLoadingState && productsInCart.length === 0 ? (
					<Box sx={{ width: "100%" }}>
						<Alert severity="info" className="empty-cart-alert">
							{t("ALERT_MESSAGES.CART_MESSAGE")}
						</Alert>
						<CartUpload onSummary={setPendingMixedSummary} />
					</Box>
				) : (
					<>
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								gap: "24px",
								mt: 3,
								position: "relative",
							}}
						>
							<Box sx={{ flex: 1 }}>
								<Card
									sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: "10px" }}
								>
									<CardContent>
										<CartTable
											groupedCartProducts={groupedCartProducts}
											cartItems={cartItems}
											selectedSite={selectedSite}
											selectedSiteCurrency={selectedSiteCurrency}
											maxQtyAllowed={maxQtyAllowed}
											quantityInputs={quantityInputs}
											handleInputChange={handleInputChange}
											handleInputBlur={handleInputBlur}
											handleKeyPress={handleKeyPress}
											handleKeyDown={handleKeyDown}
											handleQuantityChange={handleQuantityChange}
											handleRemoveItem={handleRemoveItem}
											parsePrice={parsePrice}
											handleNavigateToProduct={handleNavigateToProduct}
											t={t}
										/>
									</CardContent>
								</Card>

								<Button
									variant="outlined"
									color="primary"
									sx={{ mt: 2 }}
									startIcon={<RemoveShoppingCartOutlined />}
									onClick={() => {
										clearCart();
										setCartProducts([]);
										setQuantityInputs({});
									}}
								>
									{t("BUTTON.CLEAR_CART")}
								</Button>

								<CartUpload onSummary={setPendingMixedSummary} />

								{recommendedProducts.length > 0 && (
									<Box sx={{ mt: 2 }}>
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "flexStart",
												justifyContent: "flexStart",
											}}
										>
											<Typography variant="h6">
												{t("UI_TEXT.FREQUENTLY_TOGETHER")}
											</Typography>
											<div
												style={{
													background: "var(--primary-main)",
													height: "2px",
													margin: "2px 0",
													width: "90px",
												}}
											/>
										</div>
										<Box>
											{recommendedProducts
												.slice(0, 5)
												.filter((product) => product !== null && product !== undefined)
												.map((product) => (
													<TopProductListItem
														key={product.product_id}
														product={product}
														machine_id={product.machine_id}
													/>
												))}
										</Box>
									</Box>
								)}
							</Box>

							<Box sx={{ flex: 0.4 }}>
								<Card
									sx={{
										boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
										borderRadius: "10px",
										position: "sticky",
										top: DEFAULT_LAYOUT === "layout2" ? "150px" : "100px",
									}}
								>
									<CardContent>
										{showDisclaimerCheckout && (
											<Box mt={1}>
												<Alert severity="warning">
													<Typography fontWeight="bold">
														{t("UI_TEXT.IMPORTANT_NOTICE_FINAL_SALE")}
													</Typography>
													<Typography sx={{ fontSize: "11px" }}>
														{t("UI_TEXT.CUSTOM_MANUFACTURED_DISCLAIMER")}
													</Typography>
													<Typography mt={1} sx={{ fontSize: "11px" }}>
														<strong>{t("UI_TEXT.NO_RETURNS")}</strong>{" "}
														{t("UI_TEXT.FINAL_SALE")}
													</Typography>
												</Alert>
											</Box>
										)}
										<Box className="summary-actions">
											<Button
												variant="contained"
												color="primary"
												fullWidth
												className="checkout-button"
												data-tour="cart-checkout-button"
												onClick={() => navigate(ROUTES.CHECKOUT)}
												disabled={
													!canPlaceOrder ||
													calculateTotal().toFixed(2) <
														(parseFloat(appOptions.minOrderAmount) || parseFloat(0))
												}
											>
												{t("BUTTON.CHECKOUT")}
											</Button>
											{calculateTotal().toFixed(2) <
												(parseFloat(appOptions.minOrderAmount) || parseFloat(0)) && (
												<Typography variant="body1" color="error">
													{t("UI_TEXT.MINIMUM_ORDER_AMOUNT_SHOULD_BE")}
												</Typography>
											)}
											{!canPlaceOrder && (
												<Typography variant="body1" color="error">
													{t("UI_TEXT.YOU_ARE_NOT_AUTHORIZED_ORDER")} <br />
													{t(
														"UI_TEXT.PLEASE_CONTACT_THE_CUTOMER_CARE_FOR_FURTHER_DETAILS"
													)}
												</Typography>
											)}
										</Box>
									</CardContent>
								</Card>
							</Box>
						</Box>
					</>
				)}
			</Container>
		</>
	);
};

export default Cart;
